import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./KeystonePaywall.module.scss"

/**
 * KeystonePaywall (`/keystone`) — an original, self-contained EXPIRATION /
 * RENEWAL paywall for Private Photo Vault Premium.
 *
 * Divergence brief: claude-paywalls/_ledger.md · concept doc: claude-paywalls/keystone.md
 * The three prior expiration concepts are Deep Freeze (cold fear + countdown/meter),
 * Unbroken (warm calendar streak) and Ahead (forking road / future-self). This is
 * none of them, on every axis:
 *   - Layout:      an ARCHITECTURAL MASONRY ARCH built from the user's memory
 *                  photos (voussoirs) with a missing apex KEYSTONE + a stone tray.
 *                  No prior paywall used architecture — not a hero+card, calendar,
 *                  road, receipt, chat, map, book, before/after, pager, net, mosaic,
 *                  device-stage, player, editor, or line/conveyor.
 *   - Interaction: DRAG-AND-DROP a single locking capstone into the apex slot; it
 *                  seats and a warm "set" wave courses down both legs. (Priors used
 *                  hold, slide-to-seal-a-ring, toss/catch, flip, sweep, scrub,
 *                  divider-drag — never place-the-one-locking-piece.)
 *   - Register:    grounded / load-bearing reassurance — "the piece that holds it
 *                  all up." Not fear, not streak-pride, not hopeful-road.
 *   - Art:         LIGHT warm sandstone + PHOTOGRAPHIC voussoirs (Unsplash memories);
 *                  brand blue = the SET / protected mortar-glow, cool grey = the
 *                  un-set / paused stones. Architectural, not dark/skeuomorphic.
 *   - Value frame: CONTRAST + COMPLETION + honest loss-aversion for RENEWAL — "one
 *                  piece keeps the whole thing standing; keep the protection in place
 *                  and everything you've built stays kept, automatically."
 *
 * Honest & App Store compliant: renewal keeps real Premium features running
 * (automatic full-resolution backup, private/encrypted, organized on every device).
 * The arch NEVER falls and nothing is deleted on expiration — the copy says so
 * outright ("nothing's lost", "your photos are always yours"). No fake countdown,
 * no fake scarcity, real savings arithmetic. Yearly is the featured, default plan
 * and carries the largest price; monthly is small and secondary. The stone photos
 * are labelled as Unsplash demo memories, not the user's library.
 */

const clamp = (v: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, v))

type PlanKey = "yearly" | "monthly"

// ── Arch geometry (a segmental arch, apex at the top) ──────────────────────
const R = 150 // circle radius, px
const CY = 206 // circle centre Y within the stage, px
const STEP = 15 // degrees between stones
const TRAY_TOP = 250 // where the loose keystone rests, px from stage top
const SLOT_TOP = CY - R // apex slot centre Y, px
const SEAT_DY = SLOT_TOP - TRAY_TOP // travel from tray up into the slot (negative)

// Eight voussoirs — the everyday memories the arch is built from. `i` is the
// stone's index out from the apex (apex keystone = 0); the sign puts it on the
// left (+) or right (−) leg. Photos are bundled Unsplash demo memories.
const STONES = [
  { i: 4, src: "/keystone/s1.jpg", tag: "First steps" },
  { i: 3, src: "/keystone/s2.jpg", tag: "Beach, July" },
  { i: 2, src: "/keystone/s3.jpg", tag: "Turning five" },
  { i: 1, src: "/keystone/s4.jpg", tag: "Road trip" },
  { i: -1, src: "/keystone/s5.jpg", tag: "Sunday bakes" },
  { i: -2, src: "/keystone/s6.jpg", tag: "The ridge" },
  { i: -3, src: "/keystone/s7.jpg", tag: "Biscuit" },
  { i: -4, src: "/keystone/s8.jpg", tag: "Day one" },
] as const

// where each stone flips from "paused" to "set" as the wave travels outward
const setThreshold = (i: number) => clamp((Math.abs(i) - 0.5) / 4, 0, 1)

const stoneStyle = (i: number) => {
  const deg = 90 + i * STEP
  const rad = (deg * Math.PI) / 180
  const x = R * Math.cos(rad) // signed px offset from centre
  const top = CY - R * Math.sin(rad)
  return {
    left: `calc(50% + ${x.toFixed(1)}px)`,
    top: `${top.toFixed(1)}px`,
    transform: `translate(-50%, -50%) rotate(${(-i * STEP).toFixed(1)}deg)`,
  } as React.CSSProperties
}

const Shield = ({ size = 18 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
    <path
      d="M12 2.6l6.6 2.5v5.2c0 4.3-2.8 7.7-6.6 9.1-3.8-1.4-6.6-4.8-6.6-9.1V5.1L12 2.6z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinejoin="round"
    />
    <path
      d="M9.2 12.1l2 2 3.6-3.9"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const Check = ({ size = 12 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
    <path
      d="M5 12.5l4.2 4.2L19 7"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const KeystonePaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$3.00"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const hasTrial = yearlyProduct?.hasTrial ?? true

  const savingsPct = useMemo(() => {
    const y = yearlyProduct?.price ?? 35.99
    const m = monthlyProduct?.price ?? 13.99
    if (!m) return 0
    return Math.round((1 - y / (m * 12)) * 100)
  }, [yearlyProduct, monthlyProduct])

  // Library size for the counter — real debug data if present, else a stand-in.
  const total = useMemo(() => {
    const n = state.productData?.numPhotos
    return n && n > 200 ? n : 2431
  }, [state.productData])

  const [plan, setPlan] = useState<PlanKey>("yearly")

  // ── The keystone gesture ────────────────────────────────────────────────
  // The stone rests in a tray; drag it up into the apex slot to SEAT it. Once
  // seated, `p` tweens 0→1 and the "set" wave sweeps down both legs.
  const stageRef = useRef<HTMLDivElement | null>(null)
  const [pos, setPos] = useState({ dx: 0, dy: 0 }) // px offset from tray home
  const [dragging, setDragging] = useState(false)
  const [seated, setSeated] = useState(false)
  const [near, setNear] = useState(false)
  const [nudged, setNudged] = useState(false) // has the user grabbed it yet
  const [p, setP] = useState(0)

  const dragRef = useRef<{ sx: number; sy: number; dx: number; dy: number }>({
    sx: 0,
    sy: 0,
    dx: 0,
    dy: 0,
  })
  const pRef = useRef(0)
  const rafRef = useRef<number | undefined>(undefined)
  const startRef = useRef<number | null>(null)

  const nearSlot = (dx: number, dy: number) =>
    Math.hypot(dx - 0, dy - SEAT_DY) < 48

  const runWave = useCallback(() => {
    const dur = 1500
    startRef.current = null
    const step = (ts: number) => {
      if (startRef.current == null) startRef.current = ts
      const t = clamp((ts - startRef.current) / dur, 0, 1)
      // ease-out so the last stones settle gently
      const eased = 1 - Math.pow(1 - t, 2)
      pRef.current = eased
      setP(eased)
      if (t < 1) {
        rafRef.current = requestAnimationFrame(step)
      } else {
        rafRef.current = undefined
      }
    }
    rafRef.current = requestAnimationFrame(step)
  }, [])

  const seat = useCallback(() => {
    setSeated(true)
    setNear(false)
    setDragging(false)
    setPos({ dx: 0, dy: SEAT_DY })
    // let the stone visibly land before the wave sets the arch
    window.setTimeout(runWave, 240)
  }, [runWave])

  // Auto-demo once: lift the keystone toward the slot and set it back down, so
  // the gesture is obvious. Reduced motion → seat it immediately.
  useEffect(() => {
    if (reduceMotion) {
      setSeated(true)
      setNudged(true)
      setPos({ dx: 0, dy: SEAT_DY })
      pRef.current = 1
      setP(1)
      return
    }
    const t1 = window.setTimeout(() => {
      setPos((cur) =>
        cur.dx === 0 && cur.dy === 0 ? { dx: 0, dy: SEAT_DY * 0.42 } : cur,
      )
    }, 750)
    const t2 = window.setTimeout(() => {
      setPos((cur) => (!seated && !dragging ? { dx: 0, dy: 0 } : cur))
    }, 1350)
    return () => {
      window.clearTimeout(t1)
      window.clearTimeout(t2)
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduceMotion])

  const onDown = useCallback(
    (e: React.PointerEvent) => {
      if (seated) return
      setNudged(true)
      setDragging(true)
      dragRef.current = { sx: e.clientX, sy: e.clientY, dx: pos.dx, dy: pos.dy }
      try {
        e.currentTarget.setPointerCapture(e.pointerId)
      } catch {
        /* no-op */
      }
    },
    [seated, pos],
  )

  const onMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragging || seated) return
      const d = dragRef.current
      const dx = clamp(d.dx + (e.clientX - d.sx), -150, 150)
      const dy = clamp(d.dy + (e.clientY - d.sy), SEAT_DY - 26, 60)
      setPos({ dx, dy })
      setNear(nearSlot(dx, dy))
    },
    [dragging, seated],
  )

  const onUp = useCallback(() => {
    if (!dragging || seated) return
    setDragging(false)
    if (nearSlot(pos.dx, pos.dy)) {
      seat()
    } else {
      setPos({ dx: 0, dy: 0 }) // glide back to the tray (CSS transition)
      setNear(false)
    }
  }, [dragging, seated, pos, seat])

  const onKey = useCallback(
    (e: React.KeyboardEvent) => {
      if (seated) return
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault()
        setNudged(true)
        seat()
      }
    },
    [seated, seat],
  )

  const kept = Math.round(p * total)
  const pct = Math.round(p * 100)

  const handleClose = () => native.postNativeEvent("close")
  const handleSubscribe = () => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
  }

  const keystoneTransform = `translate(calc(-50% + ${pos.dx}px), calc(-50% + ${pos.dy}px))`

  return (
    <div className={styles.root}>
      <div className={styles.paper} aria-hidden="true" />

      <header className={styles.top}>
        <div className={styles.brand}>
          <span className={styles.brandMark}>
            <Shield size={15} />
          </span>
          <span className={styles.brandText}>
            Private Photo Vault<em>Premium</em>
          </span>
        </div>
        <button
          className={styles.close}
          onClick={handleClose}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
            <path
              d="M5 5l14 14M19 5L5 19"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </header>

      <div className={styles.intro}>
        <span className={styles.eyebrow}>
          <span className={styles.eyebrowDot} /> Premium · renewal
        </span>
        <h1 className={styles.title}>
          <span
            className={`${styles.titleLine} ${seated ? styles.titleOut : styles.titleIn}`}
          >
            One piece keeps
            <br />
            it all standing.
          </span>
          <span
            className={`${styles.titleLine} ${styles.titleOverlay} ${seated ? styles.titleIn : styles.titleOut}`}
            aria-hidden={!seated}
          >
            Set. Your whole
            <br />
            library stays kept.
          </span>
        </h1>
      </div>

      {/* ── THE ARCH ─────────────────────────────────────────────────────── */}
      <div className={styles.readout}>
        <span
          className={`${styles.statePill} ${seated ? styles.statePillOn : ""}`}
        >
          <span className={styles.stateDot} />
          {seated ? "Protection active" : "Protection paused"}
        </span>
        <span className={styles.count}>
          <strong>{kept.toLocaleString()}</strong> / {total.toLocaleString()}{" "}
          memories kept
        </span>
      </div>

      <div
        ref={stageRef}
        className={`${styles.stage} ${seated ? styles.stageSet : ""}`}
        role="img"
        aria-label={`A memory arch, ${pct}% protected. ${seated ? "Keystone set." : "Keystone not yet set."}`}
      >
        {/* the mortar arc that lights up as the arch is set */}
        <div className={styles.mortar} style={{ ["--p" as any]: p }} />

        {/* voussoir photos */}
        {STONES.map((s) => {
          const isSet = p >= setThreshold(s.i)
          return (
            <figure
              key={s.src}
              className={`${styles.stone} ${isSet ? styles.stoneSet : ""}`}
              style={stoneStyle(s.i)}
            >
              <img src={s.src} alt="" draggable={false} loading="eager" />
              <span className={styles.stoneWash} aria-hidden="true" />
              <span className={styles.stoneSeam} aria-hidden="true" />
              <figcaption className={styles.stoneTag}>{s.tag}</figcaption>
            </figure>
          )
        })}

        {/* springer stones + ground line the legs rest on */}
        <span className={styles.abutmentL} aria-hidden="true" />
        <span className={styles.abutmentR} aria-hidden="true" />
        <span className={styles.ground} aria-hidden="true" />

        {/* the apex slot — a keystone-shaped gap */}
        <span
          className={`${styles.slot} ${near ? styles.slotNear : ""} ${seated ? styles.slotFilled : ""}`}
          aria-hidden="true"
        />

        {/* the draggable keystone */}
        <button
          type="button"
          className={`${styles.keystone} ${dragging ? styles.keystoneDrag : ""} ${seated ? styles.keystoneSet : ""} ${!nudged && !seated ? styles.keystoneIdle : ""}`}
          style={{ transform: keystoneTransform }}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={onUp}
          onKeyDown={onKey}
          aria-label="Drag the keystone into the arch to keep your protection in place"
        >
          <span className={styles.keystoneFace}>
            <Shield size={22} />
          </span>
          <span className={styles.keystoneGlow} aria-hidden="true" />
        </button>

        {/* gesture hint that lives under the tray */}
        <span
          className={`${styles.hint} ${seated ? styles.hintDone : ""}`}
          aria-live="polite"
        >
          {seated
            ? "Locked in — keystone set"
            : near
              ? "Let go to set it"
              : "Drag the keystone up into the arch"}
        </span>
      </div>

      {/* honest renewal framing — no data-loss claim */}
      <p className={`${styles.reveal} ${seated ? styles.revealOn : ""}`}>
        {seated ? (
          <>
            Load-bearing again. Every memory backed up automatically, sealed
            private, in full quality — <strong>kept for the whole year</strong>.
          </>
        ) : (
          <>
            Your Premium is the keystone. Take it out and the arch still stands
            — <strong>nothing is lost, nothing is deleted</strong> — but the
            automatic protection pauses. Set it back to keep it all standing.
          </>
        )}
      </p>

      {/* what the keystone holds up — real Premium features, not a perk grid */}
      <ul className={`${styles.holds} ${seated ? styles.holdsOn : ""}`}>
        <li style={{ ["--d" as any]: 0 }}>
          <span className={styles.holdMark}>
            <Check />
          </span>
          Automatic full-resolution backup — kept the moment you take it
        </li>
        <li style={{ ["--d" as any]: 1 }}>
          <span className={styles.holdMark}>
            <Check />
          </span>
          Private &amp; encrypted — the vault only opens for you
        </li>
        <li style={{ ["--d" as any]: 2 }}>
          <span className={styles.holdMark}>
            <Check />
          </span>
          Organized and in sync on every device you own
        </li>
      </ul>

      {/* ── PRICING: yearly featured ─────────────────────────────────────── */}
      <div className={styles.plans}>
        <button
          className={`${styles.yearly} ${plan === "yearly" ? styles.selected : ""}`}
          onClick={() => setPlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.yearlyKey} aria-hidden="true">
            <Shield size={16} />
          </span>
          <span className={styles.yearlyBody}>
            <span className={styles.yearlyHead}>
              <span className={styles.yearlyName}>
                Keep it standing · Yearly
              </span>
              {savingsPct > 0 && (
                <span className={styles.saveTag}>Save {savingsPct}%</span>
              )}
            </span>
            <span className={styles.yearlyPriceRow}>
              <span className={styles.yearlyPrice}>{yearlyPrice}</span>
              <span className={styles.yearlyUnit}>/ year</span>
            </span>
            <span className={styles.yearlyPerMo}>
              about {yearlyPerMonth}/mo, billed once a year
            </span>
          </span>
          <span className={styles.radio} aria-hidden="true" />
        </button>

        <button
          className={`${styles.monthly} ${plan === "monthly" ? styles.monthlySel : ""}`}
          onClick={() => setPlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.monthlyName}>Monthly</span>
          <span className={styles.monthlyPrice}>{monthlyPrice}/mo</span>
        </button>
      </div>

      <p className={styles.assure}>
        Your photos are always yours — Premium keeps the automatic protection
        running, it never holds your library hostage.
      </p>

      <div className={styles.footerLinks}>
        <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
        <span>·</span>
        <button onClick={() => native.postNativeEvent("privacy")}>
          Privacy
        </button>
        <span>·</span>
        <button onClick={() => native.postNativeEvent("already_purchased")}>
          Restore
        </button>
      </div>

      <p className={styles.credit}>Demo memories from Unsplash</p>

      <div className={styles.dock}>
        <button className={styles.cta} onClick={handleSubscribe}>
          <span className={styles.ctaMain}>
            {plan === "yearly"
              ? hasTrial
                ? "Set the keystone — start free"
                : "Keep it all standing"
              : "Continue monthly"}
          </span>
          <span className={styles.ctaSub}>
            {plan === "yearly"
              ? hasTrial
                ? `7 days free, then ${yearlyPrice}/yr · cancel anytime`
                : `${yearlyPrice}/yr · cancel anytime`
              : `${monthlyPrice}/mo · cancel anytime`}
          </span>
        </button>
      </div>
    </div>
  )
}

export default KeystonePaywall
